import React, { useState, useEffect } from 'react';
import { getAdminSettings, updateAdminSettings, getSettings, updateSettings } from '../../services/api';
import { AdminSettings } from '../../types';
import { MailIcon, PhoneIcon, BuildingIcon, SaveIcon, DollarSignIcon, TagIcon, ImageIcon, TruckIcon, MapPinIcon } from 'lucide-react';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<AdminSettings>({
    support_email: '',
    company_name: '',
    contact_phone: '',
    company_logo_url: '',
    smtp_server: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: ''
  });
  const [taxRate, setTaxRate] = useState(8); // Default 8%
  const [depositPercentage, setDepositPercentage] = useState(15); // Default 15%
  const [sendConfirmationEmails, setSendConfirmationEmails] = useState(true); // Default true
  const [promoBannerEnabled, setPromoBannerEnabled] = useState(false);
  const [promoBannerText, setPromoBannerText] = useState('');
  const [promoBannerCode, setPromoBannerCode] = useState('');
  const [deliveryOriginAddress, setDeliveryOriginAddress] = useState('');
  const [deliveryOriginCoords, setDeliveryOriginCoords] = useState<{ lat: number | null; lng: number | null }>({ lat: null, lng: null });
  const [deliveryFreeRadius, setDeliveryFreeRadius] = useState(10);
  const [deliveryMaxRadius, setDeliveryMaxRadius] = useState(25);
  const [deliveryRatePerMile, setDeliveryRatePerMile] = useState(5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [adminData, taxData] = await Promise.all([
        getAdminSettings(),
        getSettings()
      ]);
      setSettings(adminData);
      if (taxData.tax_rate) {
        setTaxRate(taxData.tax_rate);
      }
      if (taxData.deposit_percentage) {
        setDepositPercentage(taxData.deposit_percentage);
      }
      if (typeof taxData.send_confirmation_emails !== 'undefined') {
        setSendConfirmationEmails(taxData.send_confirmation_emails);
      }
      if (typeof taxData.promo_banner_enabled !== 'undefined') {
        setPromoBannerEnabled(taxData.promo_banner_enabled);
      }
      if (taxData.promo_banner_text) {
        setPromoBannerText(taxData.promo_banner_text);
      }
      if (taxData.promo_banner_code) {
        setPromoBannerCode(taxData.promo_banner_code);
      }
      if (taxData.delivery_origin_address) {
        setDeliveryOriginAddress(taxData.delivery_origin_address);
      }
      setDeliveryOriginCoords({
        lat: taxData.delivery_origin_lat ?? null,
        lng: taxData.delivery_origin_lng ?? null
      });
      if (typeof taxData.delivery_free_radius_miles !== 'undefined' && taxData.delivery_free_radius_miles !== null) {
        setDeliveryFreeRadius(parseFloat(taxData.delivery_free_radius_miles));
      }
      if (typeof taxData.delivery_max_radius_miles !== 'undefined' && taxData.delivery_max_radius_miles !== null) {
        setDeliveryMaxRadius(parseFloat(taxData.delivery_max_radius_miles));
      }
      if (typeof taxData.delivery_rate_per_mile !== 'undefined' && taxData.delivery_rate_per_mile !== null) {
        setDeliveryRatePerMile(parseFloat(taxData.delivery_rate_per_mile));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);
    setSaveError('');

    try {
      await updateAdminSettings(settings);
      // Saved separately so a geocoding failure on the delivery origin surfaces
      // its own message rather than being swallowed by Promise.all.
      const updated = await updateSettings({
        tax_rate: taxRate,
        deposit_percentage: depositPercentage,
        send_confirmation_emails: sendConfirmationEmails,
        promo_banner_enabled: promoBannerEnabled,
        promo_banner_text: promoBannerText,
        promo_banner_code: promoBannerCode,
        delivery_origin_address: deliveryOriginAddress,
        delivery_free_radius_miles: deliveryFreeRadius,
        delivery_max_radius_miles: deliveryMaxRadius,
        delivery_rate_per_mile: deliveryRatePerMile
      });
      setDeliveryOriginCoords({
        lat: updated?.delivery_origin_lat ?? null,
        lng: updated?.delivery_origin_lng ?? null
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error updating settings:', error);
      setSaveError(
        error?.response?.data?.detail || 'Failed to save settings. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 max-w-2xl">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Admin Settings</h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure your business information and contact details
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-green-800">Settings updated successfully!</p>
            </div>
          )}

          {saveError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800">{saveError}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <BuildingIcon className="inline h-4 w-4 mr-1" />
              Company Name
            </label>
            <input
              type="text"
              name="company_name"
              value={settings.company_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your company name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <ImageIcon className="inline h-4 w-4 mr-1" />
              Company Logo URL
            </label>
            <input
              type="url"
              name="company_logo_url"
              value={settings.company_logo_url || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com/logo.png"
            />
            <p className="text-sm text-gray-500 mt-1">
              URL to your company logo image. Used in emails and website header. Recommended size: 200x60px
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MailIcon className="inline h-4 w-4 mr-1" />
              Support Email
            </label>
            <input
              type="email"
              name="support_email"
              value={settings.support_email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="support@yourcompany.com"
            />
            <p className="text-sm text-gray-500 mt-1">
              This email will receive contact form submissions from customers
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <PhoneIcon className="inline h-4 w-4 mr-1" />
              Contact Phone (optional)
            </label>
            <input
              type="tel"
              name="contact_phone"
              value={settings.contact_phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="(404) 955-8165"
            />
            <p className="text-sm text-gray-500 mt-1">
              Customer service phone number displayed on the website
            </p>
          </div>

          {/* SMTP Email Configuration Section */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">
              <MailIcon className="inline h-5 w-5 mr-2" />
              Email Configuration (SMTP)
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Configure SMTP settings for sending emails. These settings override environment variables.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Server
                </label>
                <input
                  type="text"
                  name="smtp_server"
                  value={settings.smtp_server || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="smtp.gmail.com"
                />
                <p className="text-sm text-gray-500 mt-1">
                  SMTP server hostname (e.g., smtp.gmail.com)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Port
                </label>
                <input
                  type="number"
                  name="smtp_port"
                  value={settings.smtp_port || 587}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="587"
                />
                <p className="text-sm text-gray-500 mt-1">
                  SMTP port (587 for TLS, 465 for SSL)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Email
                </label>
                <input
                  type="email"
                  name="smtp_username"
                  value={settings.smtp_username || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your-email@gmail.com"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Email address used for SMTP authentication
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Password (App Password)
                </label>
                <input
                  type="password"
                  name="smtp_password"
                  value={settings.smtp_password || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••••••••••"
                />
                <p className="text-sm text-gray-500 mt-1">
                  For Gmail: Use an App Password (not your regular password). Generate at: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google App Passwords</a>
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <DollarSignIcon className="inline h-4 w-4 mr-1" />
              Tax Rate (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={taxRate}
              onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="8.0"
            />
            <p className="text-sm text-gray-500 mt-1">
              Sales tax rate applied to all bookings (e.g., 8.0 for 8%)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <DollarSignIcon className="inline h-4 w-4 mr-1" />
              Deposit Percentage (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={depositPercentage}
              onChange={(e) => setDepositPercentage(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="50"
            />
            <p className="text-sm text-gray-500 mt-1">
              Percentage of total due at time of booking (e.g., 50 for 50%). Remaining balance will be due later.
            </p>
          </div>

          {/* Delivery Pricing Section */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">
              <TruckIcon className="inline h-5 w-5 mr-2" />
              Delivery Pricing
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Delivery is free within the free radius. Past that, customers are charged
              per mile up to the maximum radius. Beyond the maximum, the booking page
              sends them to the contact form instead.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPinIcon className="inline h-4 w-4 mr-1" />
                  Home Base Address
                </label>
                <input
                  type="text"
                  value={deliveryOriginAddress}
                  onChange={(e) => setDeliveryOriginAddress(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="123 Main St, Marietta, GA 30060"
                />
                <p className="text-sm text-gray-500 mt-1">
                  All delivery distances are measured by driving miles from this address.
                  {deliveryOriginCoords.lat !== null && deliveryOriginCoords.lng !== null ? (
                    <span className="text-green-700">
                      {' '}Located at {Number(deliveryOriginCoords.lat).toFixed(4)}, {Number(deliveryOriginCoords.lng).toFixed(4)}.
                    </span>
                  ) : (
                    <span className="text-amber-700">
                      {' '}Not set yet - delivery fees stay at $0 until you save an address here.
                    </span>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Free Radius (miles)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={deliveryFreeRadius}
                    onChange={(e) => setDeliveryFreeRadius(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Radius (miles)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={deliveryMaxRadius}
                    onChange={(e) => setDeliveryMaxRadius(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="25"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate per Mile ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    value={deliveryRatePerMile}
                    onChange={(e) => setDeliveryRatePerMile(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="5"
                  />
                </div>
              </div>

              {deliveryMaxRadius > deliveryFreeRadius && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h5 className="text-sm font-medium text-blue-900 mb-2">Current Pricing</h5>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p>Up to {deliveryFreeRadius} miles: <strong>Free</strong></p>
                    <p>
                      Each mile past {deliveryFreeRadius}: <strong>${deliveryRatePerMile.toFixed(2)}</strong>
                    </p>
                    <p>
                      At {deliveryMaxRadius} miles (the most anyone can be charged):{' '}
                      <strong>${((deliveryMaxRadius - deliveryFreeRadius) * deliveryRatePerMile).toFixed(2)}</strong>
                    </p>
                    <p>Over {deliveryMaxRadius} miles: <strong>Directed to the contact form</strong></p>
                  </div>
                </div>
              )}

              {deliveryMaxRadius <= deliveryFreeRadius && (
                <p className="text-sm text-amber-700">
                  Max radius should be larger than the free radius, otherwise no delivery
                  fee will ever apply.
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sendConfirmationEmails}
                onChange={(e) => setSendConfirmationEmails(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-3">
                <span className="block text-sm font-medium text-gray-700">
                  <MailIcon className="inline h-4 w-4 mr-1" />
                  Send Booking Confirmation Emails
                </span>
                <span className="block text-sm text-gray-500 mt-1">
                  Automatically send confirmation emails to customers when bookings are confirmed. Email will include booking details and payment information.
                </span>
              </span>
            </label>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <label className="flex items-center cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={promoBannerEnabled}
                onChange={(e) => setPromoBannerEnabled(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-3">
                <span className="block text-sm font-medium text-gray-700">
                  <TagIcon className="inline h-4 w-4 mr-1" />
                  Enable Promotional Banner
                </span>
                <span className="block text-sm text-gray-500 mt-1">
                  Display a promotional banner at the top of the website
                </span>
              </span>
            </label>

            {promoBannerEnabled && (
              <div className="ml-7 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Banner Text
                  </label>
                  <input
                    type="text"
                    value={promoBannerText}
                    onChange={(e) => setPromoBannerText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Use code SAVE10 for Memorial Day discounts"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    The promotional message to display to customers
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Promo Code (Optional)
                  </label>
                  <input
                    type="text"
                    value={promoBannerCode}
                    onChange={(e) => setPromoBannerCode(e.target.value.toUpperCase())}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="SAVE10"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    The promo code will be highlighted in the banner
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <SaveIcon className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Contact Form Configuration</h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            The contact form on your website will send emails to the support address configured above.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Current Configuration</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>Support Email:</strong> {settings.support_email || 'Not configured'}</p>
              <p><strong>Company Name:</strong> {settings.company_name || 'Not configured'}</p>
              <p><strong>Company Logo URL:</strong> {settings.company_logo_url || 'Not configured'}</p>
              <p><strong>Phone:</strong> {settings.contact_phone || 'Not configured'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
